import { orgQueryKeys, userQueryKeys } from '@/config/queryKeys';
import { useRoleLabel } from '@/hooks/useRoleLabel';
import { apiClient } from '@/lib/api-client';
import type { Role } from '@/types';
import { BASIC_ROLES } from '@/types';
import { getErrorMessage } from '@/types/errors';
import { CheckboxGroup, Group, Loading, Tooltip } from '@nautilusbio/nb-design-system';
import Button from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save, X } from 'lucide-react';
import type { FC } from 'react';
import React, { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';

import { useUpdateUserRoles } from './hooks';
import type { OrgUser } from './types';

type EditableRolesCellProps = {
  orgId: string;
  roles: Role[];
  userName: string;
  email: string;
};

const EditableRolesCell: FC<EditableRolesCellProps> = ({ orgId, roles, userName, email: userEmail }) => {
  const { t } = useTranslation('orgs');
  const roleLabel = useRoleLabel();
  const id = useId();

  const roleOptions = useMemo(
    () => BASIC_ROLES.map(role => ({ children: roleLabel(role), name: role, value: role })),
    [roleLabel],
  );
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(roles);

  const updateRolesMutation = useUpdateUserRoles(orgId);
  const queryClient = useQueryClient();

  // Optimistic mutation for role removal
  const removeRolesMutation = useMutation({
    mutationFn: async ({ email, roles: removedRoles }: { email: string; roles: Role[] }) => {
      return apiClient.delete(`/orgs/${orgId}/users`, {
        email,
        roles: removedRoles,
      });
    },
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: orgQueryKeys.users(orgId) });
      const previousUsers = queryClient.getQueryData<OrgUser[]>(orgQueryKeys.users(orgId));
      queryClient.setQueryData(orgQueryKeys.users(orgId), (old: OrgUser[] = []) =>
        old.map(user =>
          user.email === variables.email
            ? {
                ...user,
                roles: user.roles.filter((role: Role) => !variables.roles.includes(role)),
              }
            : user,
        ),
      );
      return { previousUsers };
    },
    onError: (err, variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(orgQueryKeys.users(orgId), context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.users(orgId) });
      // Also the ['users'] root: User Details reads a shared all-users list under
      // userQueryKeys.list(), which no org-scoped key prefix-matches, and a stale miss there
      // renders as a definitive "User not found."
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });

  const isValid = selectedRoles.length > 0 && selectedRoles.toSorted().join(',') !== roles.toSorted().join(',');

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setSelectedRoles(roles);
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Determine which roles were added and which were removed
    const addedRoles = selectedRoles.filter(role => !roles.includes(role));
    const removedRoles = roles.filter(role => !selectedRoles.includes(role));

    try {
      if (addedRoles.length > 0 && removedRoles.length === 0) {
        // Only adding roles
        const mutationPromise = updateRolesMutation.mutateAsync({
          email: userEmail,
          roles: addedRoles,
        });
        toast.promise(mutationPromise, {
          loading: t('updating_roles'),
          success: () => t('roles_updated', { userName }),
        });
        await mutationPromise;
      } else if (removedRoles.length > 0 && addedRoles.length === 0) {
        // Only removing roles (optimistic update)
        const mutationPromise = removeRolesMutation.mutateAsync({
          email: userEmail,
          roles: removedRoles,
        });
        toast.promise(mutationPromise, {
          loading: t('removing_roles'),
          success: () => t('roles_removed', { userName }),
        });
        await mutationPromise;
      } else if (addedRoles.length > 0 && removedRoles.length > 0) {
        // Both adding and removing: add only the new roles, then remove the removed roles
        const mutationPromise = updateRolesMutation.mutateAsync({ email: userEmail, roles: addedRoles }).then(() =>
          removeRolesMutation.mutateAsync({
            email: userEmail,
            roles: removedRoles,
          }),
        );
        toast.promise(mutationPromise, {
          loading: t('updating_roles'),
          success: () => t('roles_updated', { userName }),
        });
        await mutationPromise;
      }
      // Always invalidate after a successful change for add only is handled by mutation hook
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('failed_update_roles', { userName }));
      toast.error(errorMessage, {
        duration: Number.POSITIVE_INFINITY,
      });
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <Stack alignItems="center" fullWidth gap="pixel" id={id} orientation="horizontal">
      {!isEditing ? (
        updateRolesMutation?.isPending ? (
          <Loading className={styles.editRowLoading} type={'inline'} />
        ) : (
          <>
            <Text as="span">{roles.map(roleLabel).join(', ')}</Text>
            <Button
              aria-label={t('edit_roles')}
              data-testid={`${id}-edit-btn`}
              onPress={handleEdit}
              size="xs"
              visual="unstyled"
            >
              <Pencil size={14} />
            </Button>
          </>
        )
      ) : (
        <Stack alignItems="center" gap="pixel" orientation="horizontal">
          <CheckboxGroup
            aria-label={t('roles_selector')}
            data-testid={`${id}-edit-group`}
            id={`${id}-edit-group`}
            onChange={val => setSelectedRoles(val as Role[])}
            options={roleOptions}
            size="xs"
            value={selectedRoles}
          />
          <Group className={styles.editRoleGroup}>
            <Tooltip
              content={t('save_roles')}
              placement="right-start"
              root={document.querySelector(`#${id}`) as HTMLElement | null}
              trigger={
                <Button
                  data-testid={`${id}-save-btn`}
                  isDisabled={!isValid}
                  onPress={handleSave}
                  size="xs"
                  visual="success"
                >
                  <Save size={14} />
                </Button>
              }
            />

            <Tooltip
              content={t('cancel_role_editing')}
              placement="right-start"
              root={document.querySelector(`#${id}`) as HTMLElement | null}
              trigger={
                <Button data-testid={`${id}-cancel-btn`} onPress={handleCancel} size="xs" visual="danger">
                  <X size={14} />
                </Button>
              }
            />
          </Group>
        </Stack>
      )}
    </Stack>
  );
};

export default EditableRolesCell;
